import React, { FC, ReactElement, useMemo } from 'react';
import { useJoin } from '../../contexts/JoinContext';
import JoinOptionButtonGroup from './JoinOptionButtonGroup';
import JoinActionButton from './JoinActionButton';
import JoinRadioButtonFormGroup from './JoinRadioButtonFormGroup';
import { ACCOUNT_TYPE_SUCCESSFUL_USER, ANNUAL_INCOME_OPTIONS, EDUCATION_OPTIONS, NET_WORTH_OPTIONS } from './constants';
import { DashlineSeparator, FlexContainer, SeparatorLine, VerticalSeparator } from './styledElements';
import JoinSkipButton from './JoinSkipButton';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { Account, AccountMaker } from '../../models/Account';
import { connect } from 'react-redux';
import TextInput from '../../atoms/TextInput';
import JoinCheckboxFormGroup from './JoinCheckboxFormGroup';
import { OCCUPATION_MAX_CHARACTER } from '../../config/constants';
import styled from '@emotion/styled';
import { getStatusColor } from '../../style';
import { handleJoinVysionEvent } from '../../utils/vysion/join';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { JoinClickVysionEventsEnum } from '../../utils/vysion/join/click';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';

const Counter = styled('div')<{ hasError: boolean }>(
  {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '16px',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  ({ hasError }) => ({
    color: !hasError ? '#595959' : getStatusColor().error,
  })
);

interface JoinProfileProfessionProps {
  account: Account;
}
const JoinProfileProfession: FC<JoinProfileProfessionProps> = ({ account }): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const {
    currentFormData,
    setCurrentFormData,
    nextStep,
    handleShowSkipModal,
    prevStep,
    stepsFormData,
    setSkippedStepCounter,
    currentStepIndex,
    isMandatoryField,
  } = useJoin();

  const isSuccessfulUser = useMemo(
    () => stepsFormData[0].account_type === ACCOUNT_TYPE_SUCCESSFUL_USER,
    [stepsFormData]
  );

  const hasCounterError = currentFormData.occupation?.length > OCCUPATION_MAX_CHARACTER;

  const allowSubmit = useMemo(() => {
    const requiredFields = isSuccessfulUser
      ? [currentFormData.education, currentFormData.income, currentFormData.net_worth]
      : [currentFormData.education];

    const hasValue = isMandatoryField()
      ? requiredFields.every(field => field !== '' && field !== null && field !== undefined)
      : requiredFields.some(field => field !== '' && field !== null && field !== undefined);

    return Boolean(hasValue) && !hasCounterError;
  }, [currentFormData]);

  const isFormCompleted = () => {
    const hasValue = (value: string | number | null): boolean => {
      if (typeof value === 'undefined' || value === null) {
        return false;
      }

      return value !== '';
    };

    if (!hasValue(currentFormData?.education)) {
      return false;
    }

    if (isSuccessfulUser && !hasValue(currentFormData.income)) {
      return false;
    }

    if (isSuccessfulUser && !hasValue(currentFormData.net_worth)) {
      return false;
    }

    return true;
  };

  const handleMixpanelEvent = (result?: string) => {
    if (!account?.isEligibleForMixpanelEvents()) {
      return;
    }

    if (!result) {
      result = isFormCompleted() ? 'Completed' : 'Skipped';
    }

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_DETAILS_STEP_2_COMPLETED,
      payload: {
        eventProperties: {
          Result: result,
        },
      },
    });
  };

  const handleNextStep = () => {
    nextStep();
    handleMixpanelEvent();
    setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: false }));
  };

  const showOccupationCounter = (
    <Counter hasError={hasCounterError}>
      {currentFormData.occupation?.length || 0}/{OCCUPATION_MAX_CHARACTER}
    </Counter>
  );

  return (
    <div>
      {!isMobile ? <DashlineSeparator /> : <div style={{ marginTop: 35 }} />}
      <JoinRadioButtonFormGroup
        label="Education"
        isRequired={isMandatoryField()}
        subLabel={'(select one)'}
        inlineSubLabel={true}
        customRadioListStyle={{ marginTop: 11 }}
      >
        <JoinOptionButtonGroup
          name="education"
          lists={EDUCATION_OPTIONS}
          value={currentFormData.education}
          onChangeHandler={item => {
            if (currentFormData.education === item.value) {
              return setCurrentFormData({
                education: null,
              });
            }
            setCurrentFormData({ education: item.value });
          }}
          customButtonGroupStyle={{ display: 'flex', gap: 10, flexFlow: 'wrap', justifyContent: 'space-between' }}
          additionalStyle={{ minWidth: '38%', marginRight: 0, marginLeft: 0, margin: 0 }}
        />
      </JoinRadioButtonFormGroup>

      <DashlineSeparator />
      <JoinCheckboxFormGroup label="Occupation" extraLabelInfo={showOccupationCounter}>
        <TextInput
          id="occupation"
          value={currentFormData.occupation}
          customStyle={{ width: '100%', paddingBottom: 0, fontSize: 14 }}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setCurrentFormData({ occupation: event.target.value });
          }}
          joinForm
          status={hasCounterError ? 'error' : 'default'}
          placeholder="Type your Occupation here..."
          hideStatusIcon
        />
      </JoinCheckboxFormGroup>

      {isSuccessfulUser && (
        <div>
          <DashlineSeparator />
          <JoinRadioButtonFormGroup
            label={`Annual Income`}
            subLabel={'(select one)'}
            inlineSubLabel={true}
            isRequired={isMandatoryField()}
            customRadioListStyle={{ marginTop: 11 }}
          >
            <JoinOptionButtonGroup
              name="income"
              lists={ANNUAL_INCOME_OPTIONS}
              value={currentFormData.income}
              onChangeHandler={item => {
                if (currentFormData.income === item.value) {
                  return setCurrentFormData({
                    income: null,
                  });
                }
                setCurrentFormData({ income: item.value });
              }}
              customButtonGroupStyle={{ display: 'flex', flexFlow: 'wrap', justifyContent: 'space-between', gap: 10 }}
              additionalStyle={{ minWidth: '38%', marginRight: 0, marginLeft: 0, margin: 0 }}
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
          <JoinRadioButtonFormGroup
            label={`Net Worth`}
            subLabel={'(select one)'}
            inlineSubLabel={true}
            isRequired={isMandatoryField()}
            customRadioListStyle={{ marginTop: 11 }}
          >
            <JoinOptionButtonGroup
              name="net_worth"
              lists={NET_WORTH_OPTIONS}
              value={currentFormData.net_worth}
              onChangeHandler={item => {
                if (currentFormData.net_worth === item.value) {
                  return setCurrentFormData({
                    net_worth: null,
                  });
                } else {
                  handleJoinVysionEvent({
                    action: VYSION_ACTIONS.CLICK,
                    event: JoinClickVysionEventsEnum.CLICK_JOIN_NET_WORTH,
                  });
                }
                setCurrentFormData({ net_worth: item.value });
              }}
              customButtonGroupStyle={{ display: 'flex', flexFlow: 'wrap', justifyContent: 'space-between', gap: 10 }}
              additionalStyle={{ minWidth: '38%', marginRight: 0, marginLeft: 0, margin: 0 }}
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        <JoinActionButton label="Next" onClick={handleNextStep} isDisabled={!allowSubmit} isDefault={true} />
        <FlexContainer>
          <VerticalSeparator>
            <JoinSkipButton label="Back" onClick={() => prevStep()} />
            {!isMandatoryField() && (
              <>
                <SeparatorLine />
                <JoinSkipButton
                  label="Skip"
                  onClick={() =>
                    handleShowSkipModal(() => {
                      handleMixpanelEvent('Skipped');
                      nextStep();
                      setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: true }));
                    })
                  }
                />
              </>
            )}
          </VerticalSeparator>
        </FlexContainer>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    account: AccountMaker.create(state.profile),
  };
};

export default connect(mapStateToProps, {})(JoinProfileProfession);
